import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Row,
  Col,
} from 'reactstrap';
import moment from 'moment';
import CustomDatePicker from '../../atoms/CustomDatePicker';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

const ClinicianTenureModal = ({
  isOpen,
  toggle,
  clinician,
  existingTenures,
  groupStartDate,
  onSave,
}) => {
  const [tenures, setTenures] = useState([]);

  useEffect(() => {
    if (existingTenures && existingTenures.length > 0) {
      setTenures(
        existingTenures.map((t) => ({
          id: t.id,
          startDate: t.startDate ? moment(t.startDate).toDate() : null,
          endDate: t.endDate ? moment(t.endDate).toDate() : null,
          isDeleted: false,
        }))
      );
    } else {
      // New clinician - create initial tenure starting today
      setTenures([
        {
          id: null,
          startDate: new Date(),
          endDate: null,
          isDeleted: false,
        },
      ]);
    }
  }, [existingTenures, isOpen]);

  const handleStartDateChange = (index, date) => {
    const updated = tenures.map((t, i) =>
      i === index ? { ...t, startDate: date } : t
    );
    setTenures(updated);
  };

  const handleEndDateChange = (index, date) => {
    const updated = tenures.map((t, i) =>
      i === index ? { ...t, endDate: date } : t
    );
    setTenures(updated);
  };

  const handleDeleteTenure = (index) => {
    const updated = [...tenures];
    if (updated[index].id) {
      // Mark for deletion if it exists in DB
      updated[index].isDeleted = true;
    } else {
      // Remove from array if new
      updated.splice(index, 1);
    }
    setTenures(updated);
  };

  const handleAddTenure = () => {
    setTenures([
      ...tenures,
      {
        id: null,
        startDate: new Date(),
        endDate: null,
        isDeleted: false,
      },
    ]);
  };

  const activeTenures = tenures.filter((t) => !t.isDeleted);

  const handleSave = () => {
    if (!clinician?.id) return;

    const hasInvalidTenures = activeTenures.some((t) => !t.startDate);
    if (hasInvalidTenures) {
      return;
    }

    const formattedTenures = tenures
      .filter((t) => !t.isDeleted || t.id) // Keep deleted ones with IDs for API
      .map((t) => ({
        id: t.id,
        clinicianId: clinician.id,
        startDate: t.startDate ? moment(t.startDate).format('YYYY-MM-DD') : null,
        endDate: t.endDate ? moment(t.endDate).format('YYYY-MM-DD') : null,
        delete: t.isDeleted,
      }));

    onSave(formattedTenures);
    // Note: Don't call toggle() here - the parent's onSave handler
    // already sets showTenureModal: false to close the modal.
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {clinician?.name} - Tenure History
      </ModalHeader>
      <ModalBody>
        {activeTenures.length === 0 ? (
          <p className="text-muted">
            No active tenures. Add a tenure period or the clinician will be removed
            from this group.
          </p>
        ) : (
          activeTenures.map((tenure) => {
            const actualIndex = tenures.findIndex((t) => t === tenure);
            return (
              <Row key={actualIndex} className="mb-3 align-items-end">
                <Col md={5}>
                  <CustomDatePicker
                    label={<span className="form-group">START DATE</span>}
                    id={`tenure-start-${actualIndex}`}
                    selected={tenure.startDate}
                    handleDateChange={(date) =>
                      handleStartDateChange(actualIndex, date)
                    }
                    minDate={groupStartDate ? moment(groupStartDate).toDate() : null}
                  />
                </Col>
                <Col md={5}>
                  <CustomDatePicker
                    label={<span className="form-group">END DATE</span>}
                    id={`tenure-end-${actualIndex}`}
                    selected={tenure.endDate}
                    handleDateChange={(date) =>
                      handleEndDateChange(actualIndex, date)
                    }
                    minDate={tenure.startDate}
                    isClearable
                    placeholder="Active indefinitely"
                  />
                </Col>
                <Col md={2}>
                  <Button
                    color="link"
                    className="text-danger"
                    onClick={() => handleDeleteTenure(actualIndex)}
                    title="Delete tenure"
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Col>
              </Row>
            );
          })
        )}
        <Button color="link" onClick={handleAddTenure} className="mt-2">
          <AddIcon fontSize="small" style={{ marginRight: 4 }} /> Add Tenure Period
        </Button>
      </ModalBody>
      <div className="d-flex justify-content-end align-items-center mb-3 px-3">
        <button
          type="button"
          className="btn btn-primary btn-border btn-color-cust mr-2 px-5 py-3 text-uppercase font-11 link-color"
          onClick={toggle}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-border primary-btn-color px-5 py-3 text-uppercase font-11"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </Modal>
  );
};

ClinicianTenureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  clinician: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  existingTenures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    })
  ),
  groupStartDate: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

ClinicianTenureModal.defaultProps = {
  clinician: null,
  existingTenures: [],
  groupStartDate: null,
};

export default ClinicianTenureModal;
